/**
 * WorkspaceDashboard
 * Main dashboard for a workspace showing stats, staff, and quick actions
 *
 * Features:
 * - Workspace overview with stats
 * - Staff list tab
 * - Invitations tab
 * - Quick actions (invite, sync roster)
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../utils/theme';
import { useWorkspace } from './WorkspaceProvider';
import { StaffManagement } from './StaffManagement';
import { InvitationManager } from './InvitationManager';
import { InviteStaffModal } from './modals/InviteStaffModal';
import { cn } from '../design-system';
import { Button } from '../v2/ui/button';
import {
  Building2,
  Users,
  Mail,
  Settings,
  RefreshCw,
  ChevronLeft,
  Plus,
  Loader2,
  Check,
  AlertCircle,
  Calendar,
  Shield,
  ArrowRight,
} from 'lucide-react';

// Tab definitions
const TABS = [
  { id: 'staff', label: 'Team', icon: Users },
  { id: 'invitations', label: 'Invitations', icon: Mail },
  { id: 'settings', label: 'Settings', icon: Settings },
];

/**
 * StatCard component
 */
const StatCard = ({ icon: Icon, label, value, subtext, color, isDarkMode }) => (
  <div className={cn(
    'p-4 rounded-xl border',
    'bg-card/30 border-border/50'
  )}>
    <div className="flex items-start justify-between">
      <div>
        <p className={cn(
          'text-sm',
          'text-gray-500'
        )}>
          {label}
        </p>
        <p className={cn(
          'text-3xl font-bold mt-1',
          'text-white'
        )}>
          {value}
        </p>
        {subtext && (
          <p className={cn(
            'text-xs mt-1',
            'text-gray-500'
          )}>
            {subtext}
          </p>
        )}
      </div>
      <div className={cn(
        'p-2 rounded-lg',
        'bg-primary/10'
      )}>
        <Icon className={cn('w-5 h-5', color)} />
      </div>
    </div>
  </div>
);

/**
 * WorkspaceDashboard component
 */
export function WorkspaceDashboard() {
  const navigate = useNavigate();
  const { id: workspaceId } = useParams();
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark

  const {
    currentWorkspace,
    staff,
    invitations,
    isAdmin,
    isLoading,
    syncRoster,
    refreshWorkspace,
    currentUserStaff,
    getPersonaById,
  } = useWorkspace();

  // State
  const [activeTab, setActiveTab] = useState('staff');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Stats
  const stats = useMemo(() => ({
    staffCount: staff.length,
    pendingInvites: invitations.filter(i => i.status === 'PENDING').length,
    acceptedInvites: invitations.filter(i => i.status === 'ACCEPTED').length,
  }), [staff, invitations]);

  // Current user's persona
  const userPersona = useMemo(() => {
    if (!currentUserStaff?.personaId) return null;
    return getPersonaById(currentUserStaff.personaId);
  }, [currentUserStaff, getPersonaById]);

  // Handle roster sync
  const handleSyncRoster = useCallback(async () => {
    setIsSyncing(true);
    setSyncResult(null);
    try {
      const result = await syncRoster();
      setSyncResult({
        success: true,
        message: `Synced ${result.playersAdded || 0} players`,
      });
      setTimeout(() => setSyncResult(null), 5000);
    } catch (err) {
      setSyncResult({
        success: false,
        message: err.response?.data?.message || 'Sync failed',
      });
    } finally {
      setIsSyncing(false);
    }
  }, [syncRoster]);

  // Handle invite success
  const handleInviteSuccess = useCallback(() => {
    setIsInviteModalOpen(false);
    refreshWorkspace();
  }, [refreshWorkspace]);

  // Loading state
  if (isLoading) {
    return (
      <div className={cn(
        'min-h-screen flex items-center justify-center',
        'bg-background'
      )}>
        <div className="text-center">
          <Loader2 className={cn(
            'w-8 h-8 animate-spin mx-auto mb-4',
            'text-primary'
          )} />
          <p className={'text-gray-400'}>
            Loading workspace...
          </p>
        </div>
      </div>
    );
  }

  // No workspace found
  if (!currentWorkspace) {
    return (
      <div className={cn(
        'min-h-screen flex items-center justify-center p-6',
        'bg-background'
      )}>
        <div className="text-center">
          <AlertCircle className={cn(
            'w-12 h-12 mx-auto mb-4',
            'text-red-400'
          )} />
          <h2 className={cn(
            'text-xl font-semibold mb-2',
            'text-white'
          )}>
            Workspace Not Found
          </h2>
          <p className={cn(
            'mb-6',
            'text-gray-400'
          )}>
            The workspace you're looking for doesn't exist or you don't have access.
          </p>
          <Button onClick={() => navigate('/workspace')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Workspaces
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      'min-h-screen',
      'bg-background'
    )}>
      {/* Header */}
      <div className={cn(
        'border-b',
        'bg-card/30 border-border/50'
      )}>
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {/* Back Button */}
              <button
                onClick={() => navigate('/workspace')}
                className={cn(
                  'p-2 rounded-lg transition-colors',
                  'hover:bg-card/50'
                )}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Workspace Icon */}
              <div className={cn(
                'w-16 h-16 rounded-xl flex items-center justify-center',
                'bg-primary/10'
              )}>
                <Building2 className={cn(
                  'w-8 h-8',
                  'text-primary'
                )} />
              </div>

              {/* Workspace Info */}
              <div>
                <h1 className={cn(
                  'text-2xl font-bold',
                  'text-white'
                )}>
                  {currentWorkspace.name || currentWorkspace.teamName}
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  <span className={cn(
                    'text-sm',
                    'text-gray-400'
                  )}>
                    {currentWorkspace.leagueCode}
                  </span>
                  {userPersona && (
                    <span className={cn(
                      'flex items-center gap-1 text-sm px-2 py-0.5 rounded-full',
                      'bg-primary/10 text-primary'
                    )}>
                      {userPersona.icon} {userPersona.name}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {isAdmin && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleSyncRoster}
                    disabled={isSyncing}
                  >
                    {isSyncing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Sync Roster
                  </Button>
                  <Button onClick={() => setIsInviteModalOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Invite
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Sync Result */}
          {syncResult && (
            <div className={cn(
              'mt-4 flex items-center gap-2 px-4 py-2 rounded-lg',
              syncResult.success
                ? 'bg-green-500/10 text-green-400'
                : 'bg-red-500/10 text-red-400'
            )}>
              {syncResult.success ? (
                <Check className="w-4 h-4" />
              ) : (
                <AlertCircle className="w-4 h-4" />
              )}
              <span className="text-sm">{syncResult.message}</span>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard
            icon={Users}
            label="Team Members"
            value={stats.staffCount}
            subtext={`${stats.staffCount === 1 ? 'person' : 'people'} with access`}
            color={'text-blue-400'}
            isDarkMode={isDarkMode}
          />
          <StatCard
            icon={Mail}
            label="Pending Invites"
            value={stats.pendingInvites}
            subtext="awaiting response"
            color={'text-yellow-400'}
            isDarkMode={isDarkMode}
          />
          <StatCard
            icon={Check}
            label="Joined"
            value={stats.acceptedInvites}
            subtext="invitations accepted"
            color={'text-green-400'}
            isDarkMode={isDarkMode}
          />
        </div>

        {/* Tabs */}
        <div className={cn(
          'flex gap-1 p-1 rounded-xl mb-6',
          'bg-card/30 border border-border/50'
        )}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const TabIcon = tab.icon;
            const badge = tab.id === 'invitations' ? stats.pendingInvites : null;

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex-1 justify-center',
                  isActive
                    ? 'bg-primary text-black'
                    : 'text-gray-400 hover:text-white'
                )}
              >
                <TabIcon className="w-4 h-4" />
                {tab.label}
                {badge > 0 && (
                  <span className={cn(
                    'px-1.5 py-0.5 rounded-full text-xs',
                    isActive
                      ? 'bg-black/20 text-black'
                      : 'bg-yellow-500/20 text-yellow-400'
                  )}>
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className={cn(
          'rounded-2xl border p-6',
          'bg-card/20 border-border/50'
        )}>
          {activeTab === 'staff' && <StaffManagement />}
          {activeTab === 'invitations' && <InvitationManager />}
          {activeTab === 'settings' && (
            <div className="text-center py-12">
              <Settings className={cn(
                'w-12 h-12 mx-auto mb-4 opacity-50',
                'text-gray-500'
              )} />
              <p className={'text-gray-400'}>
                Workspace settings coming soon
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Invite Modal */}
      <InviteStaffModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onSuccess={handleInviteSuccess}
      />
    </div>
  );
}

export default WorkspaceDashboard;
